import React, { useEffect, useMemo } from 'react'
import { List, Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { getBoundActions } from '../../actions/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { isBrowser } from '../../config/environment'
import { getPrimary } from '../../services/languages/index'
import { getPmScreenSectionSectionIds } from '../../services/pm-screen/index'
import { SCREEN_TYPE_AMA_EVENTS } from '../../services/page-manager/entities/screenType'
import {
  getPmSectionType,
  TYPE_CONTENT_LIST,
  TYPE_CONTENT_REFERENCES_LIST,
  TYPE_PROMOTION,
} from '../../services/pm-section/index'
import { LIVE_ACCESS_SUBSCRIPTION_CODE } from '../../services/live-access-events/constants'
import { getSeo } from '../../services/url/index'
import { H6 } from '../Heading/index'
import SectionTilesList from '../SectionTilesList/index'
import SectionPromotion from '../SectionPromotion/index'
import LighthouseIcon from '../Icon/icons/LightHouse/index'
import MindIcon from '../Icon/icons/Mind/index'
import Meditation from '../Icon/icons/Meditation/index'
import MetaphysicsIcon from '../Icon/icons/Metaphysics/index'
import EventsAMAList, { isEventCancelled } from './EventsAMAList'

const amaOptinLandingPageUrl = 'https://www.gaia.com/lp/ama-optin'

/**
 * AMA list visibility: show until endDate (includes in-progress AMAs), not only before startDate.
 * @param {import('immutable').Map} eventDetail
 * @returns {boolean}
 */
function isUpcomingAmaEvent (eventDetail) {
  if (!Map.isMap(eventDetail)) {
    return false
  }
  const startDate = eventDetail.get('startDate')
  const endDate = eventDetail.get('endDate')
  if (!startDate || !endDate) {
    return false
  }
  if (isEventCancelled(startDate)) {
    return false
  }
  return new Date(endDate).getTime() > Date.now()
}

function buildAmaPageSections (sectionIds, pmSection) {
  const promos = []
  const tiles = []
  if (List.isList(sectionIds) && !sectionIds.isEmpty()) {
    sectionIds.forEach((id) => {
      const t = getPmSectionType({ pmSection, sectionId: id })
      if (t === TYPE_PROMOTION) {
        promos.push(id)
      } else if (t === TYPE_CONTENT_REFERENCES_LIST || t === TYPE_CONTENT_LIST) {
        tiles.push({ sectionId: id, sectionType: t })
      }
    })
  }
  // When there are 3+ promotions (in screen order), use: [0] hero, [1] between Upcoming and
  // Past tile rows, [2] mid. When there are 2, [1] is the existing mid (between Upcoming and
  // tiles) — no separate "between" row, matching legacy PM layouts.
  if (process.env.NODE_ENV === 'development' && promos.length > 3) {
    // eslint-disable-next-line no-console
    console.warn(
      '[EventsAMAPage] More than three promotion rows on ama-events; only the first three are used (hero, between, mid).',
    )
  }
  return {
    heroPromoId: promos[0],
    betweenUpcomingAndTilesPromoId: promos.length >= 3 ? promos[1] : null,
    midPromoId: promos.length >= 3 ? promos[2] : promos[1],
    tileDescriptors: tiles,
  }
}

function EventsAMAPage (props) {
  const {
    auth,
    history,
    location,
    sectionIds = List(),
    pmSection = Map(),
    setPageSeo,
    getAMAEventList,
    path,
    language,
    eventsList = List(),
    staticText = Map(),
    email = '',
    isEventsPlusMember = false,
  } = props
  const isLoggedIn = getAuthIsLoggedIn(auth)

  const {
    heroPromoId,
    betweenUpcomingAndTilesPromoId,
    midPromoId,
    tileDescriptors,
  } = useMemo(
    () => buildAmaPageSections(sectionIds, pmSection),
    [sectionIds, pmSection],
  )

  const upcomingEventsList = useMemo(() => {
    if (!List.isList(eventsList) || eventsList.isEmpty()) {
      return List()
    }
    return eventsList.filter(isUpcomingAmaEvent)
  }, [eventsList])

  // Redirect to landing page if not logged in and on browser
  useEffect(() => {
    if (!isLoggedIn && isBrowser()) {
      global.location.href = amaOptinLandingPageUrl
    }
  }, [isLoggedIn])

  useEffect(() => {
    const seo = getSeo({
      pathname: path,
      loggedIn: getAuthIsLoggedIn(auth),
    })
    setPageSeo({
      ...seo,
      location: path,
    })
  }, [path, setPageSeo, auth])

  // Get the AMA event list
  useEffect(() => {
    getAMAEventList(language)
  }, [language, getAMAEventList])

  if (!isLoggedIn) {
    return null
  }

  let nextRowPlacement = 0
  const heroRowPlacement = heroPromoId ? 0 : null
  if (heroPromoId) {
    nextRowPlacement = 1
  }
  const betweenUpcomingAndTilesRowPlacement = betweenUpcomingAndTilesPromoId
    ? nextRowPlacement
    : null
  if (betweenUpcomingAndTilesPromoId) {
    nextRowPlacement += 1
  }
  const midRowPlacement = midPromoId ? nextRowPlacement : null
  if (midPromoId) {
    nextRowPlacement += 1
  }
  const firstTileRowPlacement = nextRowPlacement

  return (
    <div className="events-ama-page">
      {heroPromoId && (
        <div className="events-ama-page__hero-promo">
          <SectionPromotion
            key={heroPromoId}
            sectionId={heroPromoId}
            history={history}
            sectionIndex={0}
            rowPlacement={heroRowPlacement}
          />
        </div>
      )}
      <div className="events-ama-page__events-list-container">
        {upcomingEventsList.size > 0 && (
          <EventsAMAList
            eventsList={upcomingEventsList}
            language={language}
            staticText={staticText}
            email={email}
            isEventsPlusMember={isEventsPlusMember}
          />
        )}
      </div>
      {betweenUpcomingAndTilesPromoId && (
        <div className="events-ama-page__between-promo">
          <SectionPromotion
            key={betweenUpcomingAndTilesPromoId}
            sectionId={betweenUpcomingAndTilesPromoId}
            history={history}
            sectionIndex={1}
            rowPlacement={betweenUpcomingAndTilesRowPlacement}
          />
        </div>
      )}
      {midPromoId && (
        <div className="events-ama-page__mid-promo">
          <SectionPromotion
            key={midPromoId}
            sectionId={midPromoId}
            history={history}
            sectionIndex={betweenUpcomingAndTilesPromoId ? 2 : 1}
            rowPlacement={midRowPlacement}
          />
        </div>
      )}
      <div className="events-ama-page__section-tiles-list-container">
        {tileDescriptors.map((tile, tileIndex) => {
          const { sectionId, sectionType } = tile
          const idxInOrder = List.isList(sectionIds)
            ? sectionIds.indexOf(sectionId)
            : -1
          const prevId = idxInOrder > 0 ? sectionIds.get(idxInOrder - 1) : null
          const prevSectionType = prevId
            ? getPmSectionType({ pmSection, sectionId: prevId })
            : null
          return (
            <div
              className="events-ama-page__section-tiles-list-tiles"
              key={sectionId}
            >
              <SectionTilesList
                sectionId={sectionId}
                sectionIndex={tileIndex}
                rowPlacement={firstTileRowPlacement + tileIndex}
                uiLocation="AMA Events"
                location={location}
                screenType={SCREEN_TYPE_AMA_EVENTS}
                sectionType={sectionType}
                prevSectionType={prevSectionType}
              />
            </div>
          )
        })}
      </div>
      <div className="events-ama-page__about">
        <div className="events-ama-page__main-container">
          <div className="events-ama-page__description-container">
            <p className="events-ama-page__description">
              {staticText.getIn(['data', 'gaias'])} <em>{staticText.getIn(['data', 'askMeAnything'])}</em> {staticText.getIn(['data', 'askMeAnythingDescription'])} {staticText.getIn(['data', 'askMeAnythingDescription2'])}
            </p>
          </div>
        </div>
        <div className="events-ama-page__values-list-wrapper">
          <ul className="events-ama-page__values-list">
            <li className="events-ama-page__values-list-item">
              <MetaphysicsIcon size={40} />
              <div className="events-ama-page__values-list-item-content">
                <H6 className="events-ama-page__values-list-item-title">{staticText.getIn(['data', 'exploreTopicsThatBroughtYouHere'])}</H6>
                <p className="events-ama-page__values-list-item-description">
                  {staticText.getIn(['data', 'exploreTopicsThatBroughtYouHereDescription'])}
                </p>
              </div>
            </li>
            <li className="events-ama-page__values-list-item">
              <MindIcon size={40} />
              <div className="events-ama-page__values-list-item-content">
                <H6 className="events-ama-page__values-list-item-title">{staticText.getIn(['data', 'engageLiveWithTrustedExperts'])}</H6>
                <p className="events-ama-page__values-list-item-description">
                  {staticText.getIn(['data', 'engageLiveWithTrustedExpertsDescription'])}
                </p>
              </div>
            </li>
            <li className="events-ama-page__values-list-item">
              <Meditation size={40} />
              <div className="events-ama-page__values-list-item-content">
                <H6 className="events-ama-page__values-list-item-title">{staticText.getIn(['data', 'connectWithFellowGaiaMembers'])}</H6>
                <p className="events-ama-page__values-list-item-description">
                  {staticText.getIn(['data', 'connectWithFellowGaiaMembersDescription'])}
                </p>
              </div>
            </li>
            <li className="events-ama-page__values-list-item">
              <LighthouseIcon size={40} />
              <div className="events-ama-page__values-list-item-content">
                <H6 className="events-ama-page__values-list-item-title">{staticText.getIn(['data', 'askTheQuestionsThatMatter'])}</H6>
                <p className="events-ama-page__values-list-item-description">
                  {staticText.getIn(['data', 'askTheQuestionsThatMatterDescription'])}
                </p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      const {
        pmScreen = Map(),
        user = Map(),
        pmSection = Map(),
      } = state
      const language = getPrimary(user.getIn(['data', 'language'], List()))
      const sectionIds = getPmScreenSectionSectionIds({
        pmScreen, screenType: SCREEN_TYPE_AMA_EVENTS, language,
      }) || List()

      return {
        auth: state.auth,
        location: state.location,
        sectionIds,
        pmSection,
        path: state.resolver.get('path'),
        language,
        email: user.getIn(['data', 'mail']),
        eventsList: state.liveAccessEvents.getIn(['amaEventList', language, 'data', 'events'], List()) || List(),
        staticText: state.staticText.getIn(['data', 'eventsAMAPage'], Map()) || Map(),
        isEventsPlusMember: !!state.auth.get('subscriptions') && state.auth.get('subscriptions').includes(LIVE_ACCESS_SUBSCRIPTION_CODE),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPageSeo: actions.page.setPageSeo,
        getAMAEventList: actions.liveAccessEvents.getAMAEventList,
      }
    },
  ),
)(EventsAMAPage)
